import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Box,
  Typography,
  Fade,
} from '@mui/material';
import IconButton from '../../components/ui/IconButton';
import { Download } from '@mui/icons-material';
import { useIntl } from '../../contexts/IntlContext';
import { toast } from 'react-toastify';
import { TableEmptyState } from '../../components/table/TableEmptyState';

interface AnalyticsTableProps {
  data: any[];
  reportParams: {
    metric: string;
    startDate: string;
    endDate: string;
    granularity: string;
    hasChannelMetric: boolean;
  } | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  generateSerieLabel: (row: any) => string;
}

export function AnalyticsTable({ data, reportParams, sortBy, sortOrder, onSort, generateSerieLabel }: AnalyticsTableProps) {
  const intl = useIntl();
  const dataRef = useRef(data);
  const generateSerieLabelRef = useRef(generateSerieLabel);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    generateSerieLabelRef.current = generateSerieLabel;
  }, [generateSerieLabel]);

  const sortedTableData = useMemo(() => {
    if (!data.length) return [];
    
    const sorted = [...data];
    if (sortBy) {
      sorted.sort((a, b) => {
        let aVal, bVal;
        
        if (sortBy === "counts") {
          aVal = Number(a[sortBy]);
          bVal = Number(b[sortBy]);
        } else if (sortBy === "establishmentName") {
          aVal = generateSerieLabelRef.current(a);
          bVal = generateSerieLabelRef.current(b);
        } else {
          aVal = a[sortBy];
          bVal = b[sortBy];
        }
        
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    
    return sorted;
  }, [data, sortBy, sortOrder]);

  const totalQuantity = useMemo(() => 
    sortedTableData.reduce((sum, row) => sum + row.counts, 0), [sortedTableData]);

  const handleDownload = useCallback(async () => {
    if (!dataRef.current || dataRef.current.length === 0) return;
    
    try {
      const metricName = intl.formatMessage({ id: `metrics.${reportParams?.metric}` });
      const csvInfo = [
        `Métrica: ${metricName}`,
        `Período: ${reportParams?.startDate} ~ ${reportParams?.endDate}`,
        `Gerado em: ${new Date().toLocaleString()}`,
        '' // Empty line
      ].join('\n');
      
      const csvData = sortedTableData
        .map(row => {
          const csvRow = [];
          if (reportParams?.granularity !== "total") csvRow.push(row.date);
          if (reportParams?.hasChannelMetric) csvRow.push(generateSerieLabelRef.current(row));
          csvRow.push(row.counts);
          return csvRow.join(',');
        })
        .join('\n');
      
      const headers = [];
      if (reportParams?.granularity !== "total") headers.push(intl.formatMessage({ id: 'analyticsV2.date' }));
      if (reportParams?.hasChannelMetric) headers.push(intl.formatMessage({ id: 'analyticsV2.channel' }));
      headers.push(intl.formatMessage({ id: 'analyticsV2.quantity' }));
      
      const csv = csvInfo + headers.join(',') + '\n' + csvData;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'analyticsV2.downloadError' }));
    }
  }, [sortedTableData, reportParams, intl]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 4, mb: 2 }}>
        <Box>
          <Typography variant="h6">
            {intl.formatMessage({ id: 'analyticsV2.reportTitle' }, { metric: intl.formatMessage({ id: `metrics.${reportParams?.metric}` }) })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: 'analyticsV2.period' }, { start: reportParams?.startDate, end: reportParams?.endDate })}
          </Typography>
        </Box>
        <IconButton onClick={handleDownload} color="primary">
          <Download />
        </IconButton>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: '65vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {reportParams?.granularity !== "total" && (
                <TableCell 
                  sx={{ cursor: "pointer", width: "14ch" }}
                  onClick={() => onSort("date")}
                >
                  {intl.formatMessage({ id: 'analyticsV2.date' })}
                  {sortBy === "date" && (sortOrder === "asc" ? " ↑" : " ↓")}
                </TableCell>
              )}
              <TableCell 
                sx={{ cursor: "pointer" }}
                onClick={() => onSort("establishmentName")}
              >
                {intl.formatMessage({ id: 'analyticsV2.description' })}
                {sortBy === "establishmentName" && (sortOrder === "asc" ? " ↑" : " ↓")}
              </TableCell>
              <TableCell 
                sx={{ cursor: "pointer", width: "10ch" }}
                onClick={() => onSort("counts")}
              >
                {intl.formatMessage({ id: 'analyticsV2.quantity' })}
                {sortBy === "counts" && (sortOrder === "asc" ? " ↑" : " ↓")}
              </TableCell>
            </TableRow>
          </TableHead>
          <Fade in key={sortedTableData.length === 0 ? "empty" : "data"} timeout={200}>
            <TableBody>
              {sortedTableData.length === 0 ? (
                <TableEmptyState colSpan={reportParams?.granularity !== "total" ? 3 : 2}>
                  {intl.formatMessage({ id: 'analyticsV2.noDataFound' })}
                </TableEmptyState>
              ) : (
                sortedTableData.map((row, index) => (
                  <TableRow key={index}>
                    {reportParams?.granularity !== "total" && <TableCell>{row.date}</TableCell>}
                    <TableCell>{generateSerieLabel(row)}</TableCell>
                    <TableCell>{row.counts}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Fade>
          <TableFooter>
            <TableRow>
              {reportParams?.granularity !== "total" && <TableCell />}
              <TableCell sx={{ fontWeight: 'bold' }}>{intl.formatMessage({ id: 'analyticsV2.total' })}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{totalQuantity}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
}